import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, ErrorMessage } from '../components/LoadingGrid';
import { Poster } from '../components/Poster';
import { api } from '../lib/api-client';
import type { UpdateEntry } from '../lib/types';

const DAY_SHORT = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

/** Kunci tanggal lokal (YYYY-MM-DD) untuk mengelompokkan rilis. */
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** 7 hari terakhir, terlama → hari ini (hari ini paling kanan). */
function lastSevenDays(): Date[] {
  const out: Date[] = [];
  const now = new Date();
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    out.push(d);
  }
  return out;
}

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function ReleaseRow({ item }: { item: UpdateEntry }) {
  const delta =
    item.previousEpisodes !== null ? item.totalEpisodes - item.previousEpisodes : 0;
  return (
    <Link
      to={`/drama/${item.platform}/${item.dramaId}`}
      className="flex items-center gap-3 rounded-2xl bg-ink-900 p-2.5 transition active:scale-[0.99]"
    >
      <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-ink-800">
        <Poster src={item.cover} title={item.title} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="line-clamp-1 text-sm font-bold">{item.title}</h3>
        <p className="mt-1 text-xs text-neutral-400">
          Episode {item.totalEpisodes}
          {delta > 0 && (
            <span className="ml-2 rounded bg-brand-500/15 px-1.5 py-0.5 text-[10px] font-bold text-brand-400">
              +{delta} baru
            </span>
          )}
        </p>
        <p className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-brand-500">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3.2 2" />
          </svg>
          {timeLabel(item.lastUpdatedAt)}
          <span className="ml-1 uppercase tracking-wide text-neutral-600">
            {item.platform}
          </span>
        </p>
      </div>
      <svg className="text-neutral-500" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}

export function JadwalPage() {
  const days = lastSevenDays();
  const [selected, setSelected] = useState(() => dayKey(new Date()));

  // Ambil banyak supaya sebaran 7 hari terisi.
  const updatesQuery = useQuery({
    queryKey: ['updates', 'jadwal'],
    queryFn: () => api.updates({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  const items = updatesQuery.data?.items ?? [];
  const forDay = items.filter((i) => dayKey(new Date(i.lastUpdatedAt)) === selected);

  // Hitung jumlah rilis per hari untuk titik penanda di strip tanggal.
  const countByDay = new Map<string, number>();
  for (const i of items) {
    const k = dayKey(new Date(i.lastUpdatedAt));
    countByDay.set(k, (countByDay.get(k) ?? 0) + 1);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 safe-top">
      <h1 className="py-3 font-display text-2xl font-extrabold">Jadwal Rilis</h1>

      {/* Strip tanggal */}
      <div className="grid grid-cols-7 gap-1.5">
        {days.map((d) => {
          const key = dayKey(d);
          const active = key === selected;
          const count = countByDay.get(key) ?? 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelected(key)}
              className={`flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-xl transition ${
                active ? 'bg-gold-grad shadow-gold' : 'bg-ink-900'
              }`}
            >
              <span
                className={`text-[10px] font-semibold ${
                  active ? 'text-[#4A3A12]' : 'text-neutral-500'
                }`}
              >
                {DAY_SHORT[d.getDay()]}
              </span>
              <span
                className={`text-[15px] font-bold ${
                  active ? 'text-gold-ink' : 'text-neutral-200'
                }`}
              >
                {d.getDate()}
              </span>
              <span
                className={`h-1 w-1 rounded-full ${
                  count > 0
                    ? active
                      ? 'bg-[#4A3A12]'
                      : 'bg-brand-500'
                    : 'bg-transparent'
                }`}
              />
            </button>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-neutral-600">
        Waktu menunjukkan kapan episode baru terdeteksi rilis oleh Dongnime.
      </p>

      {/* Daftar rilis */}
      <div className="mt-3 flex flex-col gap-2.5 pb-4">
        {updatesQuery.isPending ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-[86px] animate-pulse rounded-2xl bg-ink-900" />
          ))
        ) : updatesQuery.isError ? (
          <ErrorMessage
            title="Gagal memuat jadwal"
            message={updatesQuery.error.message}
            onRetry={() => void updatesQuery.refetch()}
          />
        ) : forDay.length === 0 ? (
          <EmptyState message="Belum ada rilis terdeteksi pada tanggal ini." />
        ) : (
          forDay.map((i) => <ReleaseRow key={`${i.platform}-${i.dramaId}`} item={i} />)
        )}
      </div>
    </div>
  );
}
